// Firestore data layer for the standalone golf Scramble scorecard.
//
// This is a deliberately isolated, throwaway feature area: one public document
// per round at `scrambles/{slug}`, read and written directly from the client
// with no auth and no tie-in to any ClassBoon teacher/student data. Access is an
// unguessable slug in the URL. See firestore.rules (`match /scrambles/{slug}`)
// for the matching security rules and src/components/scramble/ScramblePage.js
// for the UI that consumes this.
import {
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { firestore } from "../shared/FirebaseConfig";

export const HOLES = 18;
export const TEAM_IDS = ["t1", "t2", "t3"];

// A typical par-72 layout — corrected on-site via "Edit pars" if the printed
// card differs. Keys are strings because Firestore map keys are strings.
export const DEFAULT_PARS = {
  1: 4, 2: 4, 3: 3, 4: 5, 5: 4, 6: 4, 7: 3, 8: 4, 9: 4,
  10: 4, 11: 3, 12: 5, 13: 4, 14: 4, 15: 3, 16: 5, 17: 4, 18: 5,
};

// URL-friendly, unguessable token. 12 hex chars from a v4 UUID is plenty of
// entropy for a casual game and short enough to share.
export function newSlug() {
  return uuidv4().replace(/-/g, "").slice(0, 12);
}

export function scrambleRef(slug) {
  return doc(firestore, "scrambles", slug);
}

// Seed a fresh round: three default teams and a default set of pars. Uses
// setDoc so re-hitting the same slug (shouldn't happen) is idempotent-ish.
export function createRound(slug) {
  return setDoc(scrambleRef(slug), {
    courseName: "Weekend Scramble",
    courseSub: "Tap “Edit pars” to match your printed card",
    date: new Date().toISOString().slice(0, 10),
    pars: { ...DEFAULT_PARS },
    teams: {
      t1: { name: "Team 1", members: [], strokes: {} },
      t2: { name: "Team 2", members: [], strokes: {} },
      t3: { name: "Team 3", members: [], strokes: {} },
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// Live subscription to a single round. onNext receives the round data (or null
// if the doc doesn't exist); onError is optional. Returns the unsubscribe fn.
export function subscribeRound(slug, onNext, onError) {
  return onSnapshot(
    scrambleRef(slug),
    (snap) => onNext(snap.exists() ? snap.data() : null),
    (err) => {
      if (onError) onError(err);
    }
  );
}

// Update a single team's strokes for a single hole via a nested field path, so
// entering one stroke doesn't resend the whole scorecard.
export function writeScore(slug, teamId, hole, value) {
  return updateDoc(scrambleRef(slug), {
    [`teams.${teamId}.strokes.${hole}`]: value,
    updatedAt: serverTimestamp(),
  });
}

// Rename a team and set its roster of players. `members` is an array of names.
export function writeTeamMeta(slug, teamId, name, members) {
  return updateDoc(scrambleRef(slug), {
    [`teams.${teamId}.name`]: name,
    [`teams.${teamId}.members`]: members,
    updatedAt: serverTimestamp(),
  });
}

export function writePar(slug, hole, value) {
  return updateDoc(scrambleRef(slug), {
    [`pars.${hole}`]: value,
    updatedAt: serverTimestamp(),
  });
}

// Clear every team's strokes for a new round on the same link, leaving team
// names and pars untouched.
export function resetScores(slug) {
  const patch = { updatedAt: serverTimestamp() };
  TEAM_IDS.forEach((id) => {
    patch[`teams.${id}.strokes`] = {};
  });
  return updateDoc(scrambleRef(slug), patch);
}
